import { Subscription } from 'rxjs';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { FormTemplateData, TemplateTexts, MergeFieldType, MergeField } from 'src/lib/fliko/models/form-template.data';
import { LocalizationHelper } from '../../helpers/localization.helper';
import { SelectInputHelper } from '../../helpers/select-input.helper';
import { SelectInputData } from '../../interfaces/select-input-data.interface';
import { LocalizationService } from '../../services/localization.service';
import { TemplateFieldsVM } from './template-fill-form.view-model';

@Component({
  selector: 'app-template-fill-form[data][fieldsVM]',
  templateUrl: './template-fill-form.component.html',
  styleUrls: ['./template-fill-form.component.scss']
})
export class TemplateFillFormComponent<T extends FormTemplateData<U>, U extends string> implements OnInit, OnChanges, OnDestroy {
  @Input() data!: T[];
  @Input() fieldsVM!: TemplateFieldsVM<U>[];

  public templateControl = new FormControl(null);
  public templateSelect: SelectInputData<T>[] = [];

  public selected: T | null = null;
  // * We want to have empty object at this moment
  public displayFields: TemplateTexts<U> = {} as any;

  public form = this.fb.group({});

  private locHelper = new LocalizationHelper(this.ls);

  private ctrlSub$ = new Subscription();
  private subscription$ = new Subscription();

  FieldType = MergeFieldType;
  isEmpty = ObjHlp.isEmpty;

  constructor(
    private fb: FormBuilder,
    private ls: LocalizationService
  ) {}

  ngOnInit(): void {
    this.subscription$.add(this.templateControl.valueChanges.subscribe((template) => this.onTemplateSelect(template)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const data: T[] = changes.data?.currentValue;
    if (data?.length) {
      this.templateSelect = SelectInputHelper.fromValues(data, (x) => x.name);
    }
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.ctrlSub$.unsubscribe();
  }

  /** ! USED IN PARENT COMPONENT */
  public get invalid(): boolean {
    this.form.markAllAsTouched();
    return ObjHlp.isEmpty(this.displayFields) || this.form.invalid;
  }

  /** ! USED IN PARENT COMPONENT */
  public onSubmit(): TemplateTexts<U> {
    return this.displayFields;
  }

  private onTemplateSelect(template: T): void {
    this.selected = template;
    this.prepareForm();
    // * We want to have empty object at this moment
    this.displayFields = {} as any;
    for (const vm of this.fieldsVM) {
      this.displayFields[vm.name] = this.prepareText(this.selected.texts[vm.name]);
    }
  }

  private prepareForm(): void {
    const formObj: any = {};
    for (const id in this.selected?.mergeFields) {
      formObj[id] = [null, Validators.required];
    }
    this.ctrlSub$.unsubscribe();
    this.ctrlSub$ = new Subscription();
    this.form = this.fb.group(formObj);
    for (const id in this.form.controls) {
      const ctrl = this.form.controls[id];
      this.ctrlSub$.add(ctrl.valueChanges.subscribe((val) => this.updateValueOnForm(id, val)));
    }
  }

  private prepareText(text: string): string {
    if (!this.selected) return '';
    let copy = text.toString();
    for (const id in this.selected.mergeFields) {
      copy = this.replaceFieldKey(copy, id, this.fieldLabelDisplay(this.selected.mergeFields[id]));
    }
    return copy;
  }

  private updateValueOnForm(id: string, value: any): void {
    if (!this.selected) return;
    // * We want to have empty object at this moment
    const copy: TemplateTexts<U> = {} as any;
    for (const vm of this.fieldsVM) {
      copy[vm.name] = this.selected.texts[vm.name];
    }

    for (const fieldId in this.selected.mergeFields) {
      const field = this.selected.mergeFields[fieldId];
      const fieldValue = id == fieldId ? value : this.form.value[fieldId];
      let strVal: string | null = null;
      if (fieldValue) {
        switch (field.type) {
          case MergeFieldType.DATE:
            strVal = this.locHelper.localizeDate(fieldValue, 'dShort');
            break;
          case MergeFieldType.DATE_TIME:
            strVal = this.locHelper.localizeDate(fieldValue, 'dtShort');
            break;
          case MergeFieldType.NUMBER:
          case MergeFieldType.TEXT:
          case MergeFieldType.TIME:
            strVal = fieldValue.toString();
            break;
        }
      }

      for (const vm of this.fieldsVM) {
        copy[vm.name] = this.replaceFieldKey(copy[vm.name], field.id, strVal ?? this.fieldLabelDisplay(field));
      }
    }
    this.displayFields = copy;
  }

  private replaceFieldKey(text: string, key: string, value: string): string {
    const regex = new RegExp(`{${key}}`, 'g');
    return text.replace(regex, value);
  }

  private fieldLabelDisplay(field: MergeField): string {
    return `{${field.label}}`;
  }
}
